import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { ProductTable } from './ProductTable';
import { ProductForm } from './ProductForm';
import { CategoryForm } from './CategoryForm';
import type { Product } from '@/types/product';
import type { ProductCategory } from '@/types/product';
import { getStoredProducts, addStoredProduct, deleteStoredProduct, updateStoredProduct } from '../../../../utils/productStorage';
import { getStoredCategories, addStoredCategory } from '../../../../utils/categoryStorage';
import { mockProducts } from '../../../../data/mockProducts';

export function ProductManagement() {
  const [products, setProducts] = useState<Product[]>(() => {
    const storedProducts = getStoredProducts();
    return storedProducts.length > 0 ? storedProducts : mockProducts;
  });
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [showCategoryForm, setShowCategoryForm] = useState(false);

  const handleAddProduct = (newProduct: Omit<Product, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>) => {
    const product = addStoredProduct({
      ...newProduct,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current-user',
      updatedBy: 'current-user',
      attachments: [],
    });
    setProducts(prev => [...prev, product]);
    setShowProductForm(false);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setShowProductForm(true);
  };

  const handleUpdateProduct = (updatedProduct: Omit<Product, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>) => {
    if (editingProduct) {
      const product = updateStoredProduct({
        ...updatedProduct,
        attachments: editingProduct.attachments || [],
        id: editingProduct.id,
        createdAt: editingProduct.createdAt,
        createdBy: editingProduct.createdBy,
        updatedAt: new Date().toISOString(),
        updatedBy: 'current-user',
      });
      setProducts(prev => prev.map(p => p.id === editingProduct.id ? product : p));
      setShowProductForm(false);
      setEditingProduct(null);
    }
  };

  const handleDeleteProduct = (productId: string) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      deleteStoredProduct(productId);
      setProducts(prev => prev.filter(p => p.id !== productId));
    }
  };

  const handleAddCategory = (newCategory: Omit<ProductCategory, 'id' | 'createdAt' | 'updatedAt'>) => {
    const category = addStoredCategory({
      ...newCategory,
      id: newCategory.name.toLowerCase().replace(/\s+/g, '-'),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    setShowCategoryForm(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-900">Product Management</h2>
        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => setShowCategoryForm(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Category
          </button>
          <button
            type="button"
            onClick={() => {
              setEditingProduct(null);
              setShowProductForm(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Product
          </button>
        </div>
      </div>

      <ProductTable
        products={products}
        onEdit={handleEditProduct}
        onDelete={handleDeleteProduct}
      />

      {showProductForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="max-w-4xl w-full bg-white rounded-lg shadow-md">
            <ProductForm
              product={editingProduct}
              onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct}
              onCancel={() => {
                setShowProductForm(false);
                setEditingProduct(null);
              }}
            />
          </div>
        </div>
      )}
      
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-md">
            <CategoryForm
              onSubmit={handleAddCategory}
              onCancel={() => setShowCategoryForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}